import { useQuery } from '@tanstack/react-query'

import { api } from '../lib/api/client'
import { ApiError, NetworkError } from '../lib/api/errors'

/**
 * Phase 0 landing surface.
 *
 * It reads the real /health/ready endpoint — no mock data, no placeholder status.
 * When a dependency is genuinely down, this screen says so. It is replaced by the
 * authenticated app shell in Phase 3.
 */

interface ReadyResponse {
  status: 'ok' | 'degraded'
  checks: Record<string, boolean>
}

interface HealthResponse {
  status: string
  version: string
  env: string
}

export function SystemStatusPage() {
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<HealthResponse>('/health'),
    retry: 1,
  })

  const ready = useQuery({
    queryKey: ['health', 'ready'],
    queryFn: () => api.get<ReadyResponse>('/health/ready'),
    retry: 1,
    refetchInterval: 10_000,
  })

  const unreachable =
    health.error instanceof NetworkError ||
    (health.error instanceof ApiError && health.error.status >= 500)

  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--spacing-6)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 560 }}>
        <p
          style={{
            fontSize: 'var(--text-eyebrow)',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--color-text-tertiary)',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          Phase 0 · Foundation
        </p>

        <h1 style={{ marginBottom: 'var(--spacing-4)' }}>Convo</h1>

        <p
          style={{
            fontSize: 'var(--text-subtitle)',
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--spacing-8)',
            maxWidth: 'var(--layout-prose-max)',
          }}
        >
          AI Sales &amp; Customer Engagement Platform. The application shell arrives in
          Phase 3 — this screen reports the real health of the stack you just started.
        </p>

        <section
          aria-labelledby="status-heading"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-card)',
            padding: 'var(--spacing-5)',
          }}
        >
          <h2
            id="status-heading"
            style={{ fontSize: 'var(--text-body)', marginBottom: 'var(--spacing-4)' }}
          >
            System status
          </h2>

          {health.isLoading || ready.isLoading ? (
            <p style={{ color: 'var(--color-text-secondary)' }}>Checking services…</p>
          ) : unreachable ? (
            <StatusRow
              label="API"
              ok={false}
              detail="Not reachable — check .logs/api.log"
            />
          ) : (
            <>
              <StatusRow
                label="API"
                ok={health.data?.status === 'ok'}
                detail={
                  health.data ? `v${health.data.version} · ${health.data.env}` : undefined
                }
              />
              {Object.entries(ready.data?.checks ?? {}).map(([name, ok]) => (
                <StatusRow key={name} label={capitalise(name)} ok={ok} />
              ))}
            </>
          )}
        </section>

        <nav
          aria-label="Next steps"
          style={{
            marginTop: 'var(--spacing-6)',
            fontSize: 'var(--text-caption)',
            color: 'var(--color-text-tertiary)',
            lineHeight: 1.8,
          }}
        >
          <a href="http://localhost:8000/docs">API documentation</a>
          {' · '}
          <a href="http://localhost:54323">Supabase Studio</a>
          {' · '}
          <a href="http://localhost:54324">Inbucket (mail)</a>
        </nav>
      </div>
    </main>
  )
}

function StatusRow({
  label,
  ok,
  detail,
}: {
  label: string
  ok: boolean
  detail?: string | undefined
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-3)',
        padding: 'var(--spacing-3) 0',
        borderTop: '1px solid var(--color-border)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 'var(--radius-pill)',
          flexShrink: 0,
          background: ok ? 'var(--color-success)' : 'var(--color-danger)',
        }}
      />
      <span style={{ fontWeight: 500 }}>{label}</span>
      {/* State is never signalled by colour alone. */}
      <span
        style={{
          marginLeft: 'auto',
          fontSize: 'var(--text-caption)',
          color: ok ? 'var(--color-success)' : 'var(--color-danger)',
          fontWeight: 600,
        }}
      >
        {ok ? 'Healthy' : 'Down'}
      </span>
      {detail && (
        <span
          style={{
            fontSize: 'var(--text-caption)',
            color: 'var(--color-text-tertiary)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {detail}
        </span>
      )}
    </div>
  )
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
